/**
 * Pure helpers for the Monthly Pipeline tab: month arithmetic, stage state
 * derivation, and the single status→StatusPill mapping (DS v3 §4.3) shared by
 * the stage timeline, the month header, the history strip, and the tab dot.
 */

import type { StatusVariant } from '../../ui';
import { PipelineRun, PipelineStage, GamReportUploadMeta } from '../../../services/adminApi';

// ====================================
// Months
// ====================================

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** The month the team is expected to be processing right now (previous calendar month). */
export function getExpectedCycleMonth(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

/** "2026-06" → "June 2026" */
export function formatMonthDisplay(month: string): string {
  const [y, m] = month.split('-');
  const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  if (isNaN(date.getTime())) return month;
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** "2026-06" ± n months */
export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map((v) => parseInt(v, 10));
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

// ====================================
// Stage state
// ====================================

export type StageKey = keyof PipelineRun['stages'];

export function isStageDone(stage: PipelineStage | undefined): boolean {
  return stage?.status === 'completed' || stage?.status === 'confirmed';
}

export function allStagesComplete(pipeline: PipelineRun): boolean {
  return Object.values(pipeline.stages).every(
    (s) => !s || isStageDone(s) || s.status === 'skipped'
  );
}

/**
 * The reportUpload stage — the real stage when the pipeline carries one, else
 * synthesized from the month's upload metadata (legacy docs, or no pipeline yet).
 */
export function resolveReportUploadStage(
  pipeline: PipelineRun | null,
  uploadMeta: GamReportUploadMeta | null
): PipelineStage | undefined {
  const stage = pipeline?.stages.reportUpload;
  if ((!stage || stage.status === 'skipped') && uploadMeta) {
    return {
      status: 'completed',
      completedAt: uploadMeta.uploadedAt,
      notes: uploadMeta.fileName,
    };
  }
  return stage;
}

// ====================================
// GAM report set
// ====================================

export interface ReportSetStatus {
  /** Reports uploaded for the month (0 = none) */
  count: number;
  /** Reports the month is expected to have (Admin → Settings → GAM Report Uploads) */
  expected: number;
  /** Reports still missing (never negative) */
  missing: number;
}

/** How complete the month's GAM report set is ("1 of 2"). */
export function reportSetStatus(
  uploadMeta: GamReportUploadMeta | null,
  expectedReports: number
): ReportSetStatus {
  const count = uploadMeta ? Math.max(uploadMeta.sources?.length ?? 0, 1) : 0;
  const expected = Math.max(1, Math.floor(expectedReports) || 1);
  return { count, expected, missing: Math.max(0, expected - count) };
}

/** Is this stage's action currently unblocked (mirrors backend preconditions)? */
export function isStageUnblocked(pipeline: PipelineRun | null, key: StageKey): boolean {
  if (!pipeline) return false;
  switch (key) {
    case 'validation':
    case 'cmUpload':
      return pipeline.stages.dsProcessing.status === 'completed';
    case 'cmLock':
      return pipeline.stages.cmUpload.status === 'confirmed';
    case 'payoutSheets':
      return pipeline.stages.cmLock.status === 'confirmed';
    default:
      return false;
  }
}

// ====================================
// Status → pill mapping (one mapping everywhere)
// ====================================

export interface StagePill {
  variant: StatusVariant;
  label: string;
}

/**
 * The single stage-status→pill mapping. "NEEDS ACTION" (clay) marks the next
 * human step; in AUTOMATIC mode validation/payouts run themselves, so their
 * unblocked-pending state stays a neutral "QUEUED" instead.
 */
export function stagePill(
  pipeline: PipelineRun | null,
  key: StageKey,
  stage: PipelineStage | undefined
): StagePill {
  const status = stage?.status ?? 'pending';
  switch (status) {
    case 'running':
      return { variant: 'live', label: 'running' };
    case 'confirmed':
      return { variant: 'approved', label: 'confirmed' };
    case 'completed': {
      if (key === 'validation') {
        const warnings = stage?.result?.summary?.warnings ?? 0;
        if (warnings > 0) return { variant: 'pending', label: `${warnings} warning${warnings === 1 ? '' : 's'}` };
        return { variant: 'ok', label: 'passed' };
      }
      return { variant: 'ok', label: 'done' };
    }
    case 'failed': {
      if (key === 'validation') {
        const blockers = stage?.result?.summary?.blockers ?? 0;
        if (blockers > 0) return { variant: 'flagged', label: `${blockers} blocker${blockers === 1 ? '' : 's'}` };
      }
      return { variant: 'flagged', label: 'failed' };
    }
    case 'skipped':
      return { variant: 'paused', label: 'skipped' };
    default: {
      // pending
      if (!isStageUnblocked(pipeline, key)) return { variant: 'draft', label: 'waiting' };
      const isHumanGate = key === 'cmUpload' || key === 'cmLock';
      const auto = pipeline?.autoAdvance === true;
      if (isHumanGate || !auto) return { variant: 'pending', label: 'needs action' };
      return { variant: 'draft', label: 'queued' };
    }
  }
}

/** Overall pipeline rollup for the header + history chips. */
export function overallPill(pipeline: PipelineRun): StagePill {
  const stages = Object.values(pipeline.stages).filter(Boolean) as PipelineStage[];
  if (stages.some((s) => s.status === 'running')) return { variant: 'live', label: 'running' };
  if (stages.some((s) => s.status === 'failed')) return { variant: 'flagged', label: 'failed' };
  if (allStagesComplete(pipeline)) return { variant: 'ok', label: 'complete' };
  return { variant: 'pending', label: 'in progress' };
}

// ====================================
// Phase derivation (tab dot + header summary)
// ====================================

export type PipelineBadge = 'running' | 'attention' | 'failed' | null;

export interface PipelinePhase {
  /** 1-based index of the step in flight (0 = not started), out of 6. */
  stepIndex: number;
  totalSteps: number;
  label: string;
  badge: PipelineBadge;
}

const STAGE_ORDER: Array<{ key: StageKey; label: string }> = [
  { key: 'reportUpload', label: 'GAM report' },
  { key: 'dsProcessing', label: 'DS processing' },
  { key: 'validation', label: 'Validation' },
  { key: 'cmUpload', label: 'ChannelMeter upload' },
  { key: 'cmLock', label: 'Lock payments' },
  { key: 'payoutSheets', label: 'Payout sheets' },
];

/**
 * Where the month's run stands: current step, a short label, and the badge the
 * Pipeline tab dot shows from other tabs. Badge precedence: running > failed >
 * attention (a human step is up next).
 */
export function derivePipelinePhase(
  pipeline: PipelineRun | null,
  uploadMeta: GamReportUploadMeta | null,
  expectedReports = 1
): PipelinePhase {
  if (!pipeline) {
    const { missing } = reportSetStatus(uploadMeta, expectedReports);
    return {
      stepIndex: 0,
      totalSteps: STAGE_ORDER.length,
      label: !uploadMeta
        ? 'Waiting for GAM report'
        : missing > 0
          ? `Waiting for ${missing} more GAM report${missing === 1 ? '' : 's'}`
          : 'Ready to start',
      badge: null,
    };
  }

  const stageOf = (key: StageKey): PipelineStage | undefined =>
    key === 'reportUpload' ? resolveReportUploadStage(pipeline, uploadMeta) : pipeline.stages[key];

  const running = STAGE_ORDER.find((s) => stageOf(s.key)?.status === 'running');
  const failed = STAGE_ORDER.find((s) => stageOf(s.key)?.status === 'failed');

  // First not-done stage = the step in flight
  let stepIndex = STAGE_ORDER.length;
  let current: { key: StageKey; label: string } | null = null;
  for (let i = 0; i < STAGE_ORDER.length; i++) {
    const stage = stageOf(STAGE_ORDER[i].key);
    if (!isStageDone(stage) && stage?.status !== 'skipped') {
      stepIndex = i + 1;
      current = STAGE_ORDER[i];
      break;
    }
  }

  let badge: PipelineBadge = null;
  if (running) badge = 'running';
  else if (failed) badge = 'failed';
  else if (current) {
    const pill = stagePill(pipeline, current.key, stageOf(current.key));
    if (pill.label === 'needs action') badge = 'attention';
  }

  const label = running
    ? `${running.label} running`
    : failed
      ? `${failed.label} failed`
      : current
        ? `Next: ${current.label}`
        : 'Complete';

  return { stepIndex, totalSteps: STAGE_ORDER.length, label, badge };
}
